// Shared "old contents fade out while the new fade in" bookkeeping for the
// full-screen action row (left slot, primary label, desktop summary). The old
// contents are kept for a TIMER, never for `animationend`. Reduced motion
// swaps instantly (no leaving copy, no animation).
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export const BAR_CROSSFADE_MS = 200;
const SLACK_MS = 80;
const STYLE_ID = 'cvx-bar-crossfade';

/** Injects the shared fade keyframes once (`cvx-bar-in` / `cvx-bar-out`). */
export function ensureCrossfadeStyle() {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = '@keyframes cvx-bar-in { from { opacity: 0; } to { opacity: 1; } } '
    + '@keyframes cvx-bar-out { from { opacity: 1; } to { opacity: 0; } }';
  document.head.appendChild(s);
}

// A dying copy is a picture of the old contents: it must not answer to the
// live row's test ids or element ids (two `id`s break labels, and a lookup
// would find the ghost). React never updates that subtree again and removes it
// as a whole, so editing attributes is safe.
export function stripIdentity(node) {
  if (!node) return;
  node.querySelectorAll('[data-testid], [id]').forEach((el) => {
    el.removeAttribute('data-testid');
    el.removeAttribute('id');
  });
}

/**
 * @param {string|number} key - changes when the contents should crossfade.
 * @param {import('react').ReactNode} node - the current contents.
 * @returns {{leaving: {key: any, node: any}|null, animateIn: boolean}}
 */
export default function useLeavingSwap(key, node, reduced) {
  useLayoutEffect(() => { ensureCrossfadeStyle(); }, []);
  const lastRef = useRef({ key, node });
  const prev = lastRef.current;
  const animatedKey = useRef(null);
  if (prev.key !== key) animatedKey.current = key;
  const [leaving, setLeaving] = useState(null);

  useLayoutEffect(() => {
    if (prev.key !== key && !reduced) setLeaving({ key: prev.key, node: prev.node });
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  useLayoutEffect(() => { lastRef.current = { key, node }; });

  useEffect(() => {
    if (!leaving) return undefined;
    const timer = setTimeout(() => setLeaving(null), BAR_CROSSFADE_MS + SLACK_MS);
    return () => clearTimeout(timer);
  }, [leaving]);

  return { leaving, animateIn: !reduced && animatedKey.current === key };
}
